// src/features/datev/components/DatevSyncStatus.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../shared/services/supabase';

interface DatevSyncStatusProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
}

interface PendingTask {
  id: string;
  year: number;
  month: number;
  task_type: string;
  required_documents: string[];
  required_next_month_documents: string[];
  fulfilled_documents: string[];
  status: string;
  check_count: number;
  escalation_level: number;
  last_check_at: string | null;
  created_at: string;
}

interface ReplacementEvent {
  id: string;
  document_id: string;
  created_at: string;
  event_data: {
    previous_datev_timestamp?: string;
    new_datev_timestamp?: string;
  } | null;
  // Joined from datev_documents
  document_type: string;
  employee_number: number | null;
  period: string;
}

interface SyncRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  trigger_type: string;
  customer_id: string | null;
  synced_customers: number | null;
  totals_created: number;
  totals_replaced: number;
  totals_skipped: number;
  totals_failed: number;
  customer_errors: number;
  pending_tasks_newly_completed: number;
  mail_sent: boolean;
  status: string;
  error: string | null;
  // Stufe 2: Aggregierte Task-Counts aus datev_sync_tasks pro Sync-Run.
  // Wird nach dem Run-Load nachgeladen und in den Run-Eintrag injiziert.
  task_count?: { total: number; pending: number; running: number; completed: number; failed: number };
}

// Stufe 2: Aktuelle Task-Queue fuer den ausgewaehlten Mandanten.
interface SyncTaskCounts {
  pending: number;
  running: number;
  completed_today: number;
  failed_today: number;
}

// Audit-Resync: Auswahl-Modi und Konstanten
type ResyncMode = 'outdated' | 'missing';

interface ResyncPreview {
  count: number;
  sample: { document_type: string; employee_number: number | null; period: string; storage_path: string | null }[];
  ids: string[];
  storagePaths: string[];
  // Vollstaendige Liste aller betroffenen Docs (fuer metadata.affected_summary
  // im Audit-Log). Sample ist nur fuer die UI-Anzeige (max 10).
  affected: { id: string; document_type: string; employee_number: number | null; period: string; storage_path: string | null }[];
}

const ALL_DOCUMENT_TYPES = [
  'LOBN', 'LSTB', 'LSTE', 'SVNW', 'ABEG', 'AANB', 'BUBE',
  'LOJO', 'LJOE', 'LSTA', 'KBNW', 'SBNW', 'ZAKF', 'PRZA',
  'ZABR', 'ZAUW', 'LOPS', 'LOPE', 'KOST', 'KOTR', 'LOWE',
  'LOPN', 'LKTO', 'LORE', 'PDAT', 'PFAN', 'DAWE', 'WEAN'
];

const POLL_INTERVAL_IDLE_MS = 30000;
const POLL_INTERVAL_ACTIVE_MS = 5000;

export const DatevSyncStatus: React.FC<DatevSyncStatusProps> = ({
  customerId,
  beraternummer,
  mandantennummer
}) => {
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [replacements, setReplacements] = useState<ReplacementEvent[]>([]);
  const [syncRuns, setSyncRuns] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncTriggering, setSyncTriggering] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Stufe 2: Live-Counts der Task-Queue fuer diesen Mandanten
  const [taskCounts, setTaskCounts] = useState<SyncTaskCounts>({
    pending: 0,
    running: 0,
    completed_today: 0,
    failed_today: 0
  });

  // Audit-Resync State
  const [resyncPeriod, setResyncPeriod] = useState<string>('');
  const [resyncDocType, setResyncDocType] = useState<string>('all');
  const [resyncEmployeeNumber, setResyncEmployeeNumber] = useState<string>('');
  const [resyncMode, setResyncMode] = useState<ResyncMode>('outdated');
  const [resyncPreview, setResyncPreview] = useState<ResyncPreview | null>(null);
  const [resyncBusy, setResyncBusy] = useState(false);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);
  const [resyncMessageKind, setResyncMessageKind] = useState<'info' | 'success' | 'error'>('info');

  // Anzeige-Hilfen
  const formatDateTime = (iso: string | null): string => {
    if (!iso) return '-';
    return new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(iso));
  };

  const formatDuration = (ms: number | null): string => {
    if (ms === null || ms === undefined) return '-';
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    return `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')} min`;
  };

  const formatPeriod = (year: number, month: number): string =>
    `${String(month).padStart(2, '0')}.${year}`;

  const loadData = useCallback(async () => {
    try {
      // 1. Pending Tasks für diesen Kunden
      const { data: tasks, error: tasksErr } = await supabase
        .from('datev_pending_tasks')
        .select('id, year, month, task_type, required_documents, required_next_month_documents, fulfilled_documents, status, check_count, escalation_level, last_check_at, created_at')
        .eq('customer_id', customerId)
        .in('status', ['pending', 'partially_fulfilled', 'escalated'])
        .order('escalation_level', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);

      if (!tasksErr && tasks) {
        setPendingTasks(tasks as PendingTask[]);
      }

      // 2. Kritische Ersetzungen (LSTB / SVNW) für diesen Kunden, letzte 7 Tage
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const { data: customerDocs } = await supabase
        .from('datev_documents')
        .select('id, document_type, employee_number, period')
        .eq('customer_id', customerId)
        .in('document_type', ['LSTB', 'SVNW'])
        .limit(5000);

      const docIdMap = new Map<string, { document_type: string; employee_number: number | null; period: string }>();
      (customerDocs || []).forEach(d => {
        docIdMap.set(d.id, {
          document_type: d.document_type,
          employee_number: d.employee_number,
          period: d.period
        });
      });

      if (docIdMap.size > 0) {
        const docIds = Array.from(docIdMap.keys());
        const { data: events } = await supabase
          .from('datev_document_events')
          .select('id, document_id, created_at, event_data')
          .eq('event_type', 'replaced')
          .in('document_id', docIds)
          .gte('created_at', sevenDaysAgo)
          .order('created_at', { ascending: false })
          .limit(50);

        const enrichedEvents: ReplacementEvent[] = (events || []).map(e => {
          const docInfo = docIdMap.get(e.document_id);
          return {
            id: e.id,
            document_id: e.document_id,
            created_at: e.created_at,
            event_data: e.event_data,
            document_type: docInfo?.document_type || '-',
            employee_number: docInfo?.employee_number ?? null,
            period: docInfo?.period || '-'
          };
        });
        setReplacements(enrichedEvents);
      } else {
        setReplacements([]);
      }

      // 3. Sync-Runs (global, letzte 20)
      const { data: runs } = await supabase
        .from('datev_sync_runs')
        .select('id, started_at, finished_at, duration_ms, trigger_type, customer_id, synced_customers, totals_created, totals_replaced, totals_skipped, totals_failed, customer_errors, pending_tasks_newly_completed, mail_sent, status, error')
        .order('started_at', { ascending: false })
        .limit(20);

      // 4. Stufe 2: Task-Aggregation pro Sync-Run (Bulk-Query, ein Roundtrip).
      // Liefert pro run.id: { total, pending, running, completed, failed }.
      // Wird in den jeweiligen Run-Eintrag injiziert.
      let runsWithTasks: SyncRun[] = (runs as SyncRun[] | null) ?? [];
      if (runsWithTasks.length > 0) {
        const runIds = runsWithTasks.map(r => r.id);
        const { data: taskAggs, error: taskAggsErr } = await supabase
          .from('datev_sync_tasks')
          .select('sync_run_id, status')
          .in('sync_run_id', runIds)
          .limit(50000);

        if (!taskAggsErr && taskAggs) {
          const taskCountByRun = new Map<string, { total: number; pending: number; running: number; completed: number; failed: number }>();
          for (const t of taskAggs) {
            if (!t.sync_run_id) continue;
            const c = taskCountByRun.get(t.sync_run_id) || { total: 0, pending: 0, running: 0, completed: 0, failed: 0 };
            c.total++;
            if (t.status === 'pending') c.pending++;
            else if (t.status === 'running') c.running++;
            else if (t.status === 'completed') c.completed++;
            else if (t.status === 'failed') c.failed++;
            taskCountByRun.set(t.sync_run_id, c);
          }
          runsWithTasks = runsWithTasks.map(r => ({
            ...r,
            task_count: taskCountByRun.get(r.id)
          }));
        }
      }
      setSyncRuns(runsWithTasks);

      // 5. Stufe 2: Live-Counts der Task-Queue fuer diesen Mandanten.
      // Wird in der Card "DATEV-Sync-Queue" angezeigt und steuert das Polling-Intervall.
      const startOfTodayBerlin = (() => {
        const now = new Date();
        const berlinNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
        berlinNow.setHours(0, 0, 0, 0);
        // Konvertiere zurueck zu UTC ISO fuer Supabase-Vergleich
        const berlinOffsetMin = (now.getTime() - berlinNow.getTime()) / 60000 - now.getTimezoneOffset();
        return new Date(berlinNow.getTime() - berlinOffsetMin * 60000).toISOString();
      })();

      const { data: customerTasks, error: customerTasksErr } = await supabase
        .from('datev_sync_tasks')
        .select('status, finished_at')
        .eq('customer_id', customerId)
        .limit(5000);

      if (!customerTasksErr && customerTasks) {
        let pending = 0, running = 0, completedToday = 0, failedToday = 0;
        for (const t of customerTasks) {
          if (t.status === 'pending') pending++;
          else if (t.status === 'running') running++;
          else if (t.status === 'completed' && t.finished_at && t.finished_at >= startOfTodayBerlin) completedToday++;
          else if (t.status === 'failed' && t.finished_at && t.finished_at >= startOfTodayBerlin) failedToday++;
        }
        setTaskCounts({ pending, running, completed_today: completedToday, failed_today: failedToday });
      }
    } catch (err) {
      console.error('[DatevSyncStatus] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Initial Load + Polling
  useEffect(() => {
    loadData();
    // Polling: schneller wenn ein Run aktuell laeuft ODER wenn der Worker
    // gerade Tasks fuer diesen Mandanten abarbeitet (pending/running > 0).
    const hasRunningRun = syncRuns.some(r => r.status === 'running');
    const hasActiveTasks = taskCounts.pending > 0 || taskCounts.running > 0;
    const intervalMs = (hasRunningRun || syncTriggering || hasActiveTasks) ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_IDLE_MS;
    const interval = setInterval(loadData, intervalMs);
    return () => clearInterval(interval);
  }, [loadData, syncRuns, syncTriggering, taskCounts.pending, taskCounts.running]);

  /**
   * Triggert die Supabase Edge Function 'datev-sync' (Producer) fuer genau diesen Kunden.
   *
   * Stufe 2 (Job-Queue-Architektur): Der Producer erzeugt nur noch Tasks in
   * datev_sync_tasks und kehrt nach 1-3 Sekunden zurueck. Die eigentliche
   * DATEV-Arbeit erledigt der Worker (datev-sync-worker), der per pg_cron
   * jede Minute getriggert wird. Die Card "DATEV-Sync-Queue" zeigt den
   * Live-Fortschritt.
   */
  const triggerManualSync = async () => {
    setSyncTriggering(true);
    setSyncMessage('Tasks werden erzeugt...');
    try {
      const { data, error } = await supabase.functions.invoke('datev-sync', {
        body: { customerId }
      });

      if (error) {
        setSyncMessage(`Fehler beim Sync: ${error.message}`);
        return;
      }

      const summary = (data as { summary?: {
        duration_ms?: number;
        tasks_enqueued?: number;
        tasks_already_open?: number;
        synced_customers?: number;
        pending_tasks?: { newly_completed?: number };
      } } | null)?.summary;

      // Falls Producer skipt (z.B. Lock-Konflikt, kein autorisierter Mandant)
      const skipped = (data as { skipped?: boolean; reason?: string } | null);
      if (skipped?.skipped) {
        setSyncMessage(`Sync uebersprungen: ${skipped.reason ?? 'unbekannter Grund'}`);
        await loadData();
        return;
      }

      if (summary) {
        const enqueued = summary.tasks_enqueued ?? 0;
        const alreadyOpen = summary.tasks_already_open ?? 0;
        const newlyCompleted = summary.pending_tasks?.newly_completed ?? 0;

        const parts: string[] = [];
        parts.push(`Producer fertig in ${formatDuration(summary.duration_ms ?? null)}`);

        if (enqueued > 0) {
          parts.push(`${enqueued} Task${enqueued !== 1 ? 's' : ''} angelegt`);
        }
        if (alreadyOpen > 0) {
          parts.push(`${alreadyOpen} bereits offen`);
        }
        if (enqueued === 0 && alreadyOpen === 0) {
          parts.push('keine neuen Tasks (nichts zu tun)');
        }
        if (newlyCompleted > 0) {
          parts.push(`${newlyCompleted} Pending-Task${newlyCompleted !== 1 ? 's' : ''} abgeschlossen`);
        }
        if (enqueued > 0) {
          parts.push('Worker arbeitet ab. Fortschritt siehe oben.');
        }

        setSyncMessage(parts.join(' · '));
      } else {
        setSyncMessage('Sync abgeschlossen.');
      }

      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setSyncMessage(`Fehler beim Sync: ${msg}`);
    } finally {
      setSyncTriggering(false);
    }
  };

  // ============================================
  // AUDIT-RESYNC LOGIK
  // ============================================

  /**
   * Liefert true, wenn mindestens ein konkreter Filter (Periode, Doctype != all,
   * oder Mitarbeiter-Nr.) gesetzt ist. Verhindert, dass ein Vollreset des
   * gesamten Kunden-Spiegels per Klick moeglich ist.
   */
  const hasResyncFilterConcrete = (): boolean => {
    const empNumValid = resyncEmployeeNumber.trim() !== '' && !isNaN(parseInt(resyncEmployeeNumber, 10));
    return Boolean(resyncPeriod) || resyncDocType !== 'all' || empNumValid;
  };

  /**
   * Baut den Supabase-Query mit den gewaehlten Filtern auf datev_documents
   * (eingeschraenkt auf den aktuellen customerId und nicht-replaced).
   */
  const buildResyncQuery = () => {
    let query = supabase
      .from('datev_documents')
      .select('id, document_type, employee_number, period, storage_path')
      .eq('customer_id', customerId)
      .is('replaced_at', null);

    if (resyncPeriod) {
      query = query.eq('period', resyncPeriod);
    }
    if (resyncDocType !== 'all') {
      query = query.eq('document_type', resyncDocType);
    }
    if (resyncEmployeeNumber.trim() !== '') {
      const empNum = parseInt(resyncEmployeeNumber, 10);
      if (!isNaN(empNum)) {
        query = query.eq('employee_number', empNum);
      }
    }
    return query;
  };

  /**
   * Vorschau: zaehlt betroffene Dokumente und liefert eine Stichprobe (max 10).
   * Pflicht-Schritt vor Ausfuehrung.
   */
  const runResyncPreview = async () => {
    if (!hasResyncFilterConcrete()) {
      setResyncMessage('Bitte mindestens einen Filter setzen (Periode, Dokumententyp oder Personalnummer).');
      setResyncMessageKind('error');
      setResyncPreview(null);
      return;
    }

    setResyncBusy(true);
    setResyncMessage(null);
    setResyncPreview(null);

    try {
      const { data, error } = await buildResyncQuery().limit(5000);

      if (error) {
        setResyncMessage(`Fehler bei Vorschau: ${error.message}`);
        setResyncMessageKind('error');
        return;
      }

      const rows = data || [];
      const sample = rows.slice(0, 10).map(r => ({
        document_type: r.document_type,
        employee_number: r.employee_number,
        period: r.period,
        storage_path: r.storage_path
      }));
      const ids = rows.map(r => r.id);
      const storagePaths = rows
        .map(r => r.storage_path)
        .filter((p): p is string => typeof p === 'string' && p.length > 0);
      const affected = rows.map(r => ({
        id: r.id,
        document_type: r.document_type,
        employee_number: r.employee_number,
        period: r.period,
        storage_path: r.storage_path
      }));

      setResyncPreview({ count: rows.length, sample, ids, storagePaths, affected });

      if (rows.length === 0) {
        setResyncMessage('Keine Dokumente entsprechen den gewaehlten Filtern.');
        setResyncMessageKind('info');
      } else {
        setResyncMessage(`${rows.length} Dokument${rows.length === 1 ? '' : 'e'} wuerde${rows.length === 1 ? '' : 'n'} betroffen sein.`);
        setResyncMessageKind('info');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setResyncMessage(`Fehler bei Vorschau: ${msg}`);
      setResyncMessageKind('error');
    } finally {
      setResyncBusy(false);
    }
  };

  /**
   * Audit-Trail: schreibt EINEN Eintrag in datev_audit_log pro Resync-Aktion.
   *
   * Bewusst NICHT in datev_document_events: dort waere der Eintrag bei
   * missing-Modus durch ON DELETE CASCADE mit den geloeschten Documents
   * weggeraeumt worden. datev_audit_log ist eine separate, unveraenderliche
   * Tabelle ohne FK-Abhaengigkeit zu Documents.
   *
   * Liefert die action_id zurueck, damit nach der Aktion das affected_count
   * korrigiert werden koennte (aktuell nutzen wir es nicht, der Eintrag wird
   * vor der Aktion mit dem Vorschau-Wert geschrieben).
   */
  const writeAuditLogEntry = async (
    actionType: string,
    affectedCount: number,
    affectedDocs: { id: string; document_type: string; employee_number: number | null; period: string; storage_path: string | null }[]
  ): Promise<void> => {
    // actor_user_id aus aktueller Auth-Session
    let actorUserId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      actorUserId = user?.id ?? null;
    } catch {
      // wenn auth nicht ermittelbar, trotzdem Audit-Log schreiben (mit NULL)
    }

    const targetFilter = {
      customer_id: customerId,
      period: resyncPeriod || null,
      document_type: resyncDocType === 'all' ? null : resyncDocType,
      employee_number: resyncEmployeeNumber.trim() === '' ? null : parseInt(resyncEmployeeNumber, 10)
    };

    const metadata = {
      mode: resyncMode,
      beraternummer,
      mandantennummer,
      affected_summary: affectedDocs.map(d => ({
        id: d.id,
        document_type: d.document_type,
        employee_number: d.employee_number,
        period: d.period,
        storage_path: d.storage_path
      }))
    };

    const { error: auditErr } = await supabase
      .from('datev_audit_log')
      .insert({
        actor_user_id: actorUserId,
        action_type: actionType,
        target_table: 'datev_documents',
        target_filter: targetFilter,
        affected_count: affectedCount,
        metadata
      });

    if (auditErr) {
      // Audit-Log darf nicht still scheitern - das ist die Grundlage der
      // Audit-Konformitaet. Wir werfen, damit die Resync-Aktion abgebrochen
      // wird, statt unaufgezeichnet zu laufen.
      throw new Error(`Audit-Log konnte nicht geschrieben werden: ${auditErr.message}`);
    }
  };

  /**
   * Ausfuehrung der Resync-Aktion. Vorschau muss vorher gelaufen sein.
   *   outdated -> datev_timestamp auf 1970-01-01 setzen
   *               (Sync sieht: DATEV-TS > gespeicherter TS -> REPLACE)
   *   missing  -> Storage-Files loeschen, dann DB-Eintraege loeschen
   *               (Sync sieht: kein Eintrag vorhanden -> CREATE)
   * In beiden Faellen vor der eigentlichen Aktion ein Audit-Event
   * pro Dokument schreiben.
   */
  const runResyncExecute = async () => {
    if (!resyncPreview || resyncPreview.count === 0) {
      setResyncMessage('Bitte zuerst Vorschau ausfuehren.');
      setResyncMessageKind('error');
      return;
    }

    const modeLabel = resyncMode === 'outdated' ? 'als veraltet markieren' : 'aus Spiegel entfernen (inkl. Storage-Files)';
    const confirmText = `Wirklich ${resyncPreview.count} Dokument${resyncPreview.count === 1 ? '' : 'e'} ${modeLabel}?\n\nDanach manuell unten "Jetzt syncen" klicken, damit der DATEV-Spiegel neu aufgebaut wird.`;
    if (!window.confirm(confirmText)) {
      return;
    }

    setResyncBusy(true);
    setResyncMessage('Aktion laeuft...');
    setResyncMessageKind('info');

    try {
      // Audit-Trail VOR der Aktion (sonst sind die document_ids bei "missing" weg).
      // Schreibt EINEN Eintrag in datev_audit_log mit Filter, Anzahl und der
      // vollstaendigen Liste betroffener Dokumente im metadata-Feld.
      // Wenn das Audit-Log fehlschlaegt, wird hier geworfen und die Aktion
      // wird gar nicht erst ausgefuehrt - kein unaufgezeichneter Resync.
      const actionType = resyncMode === 'outdated' ? 'datev_resync_outdated' : 'datev_resync_missing';
      await writeAuditLogEntry(actionType, resyncPreview.count, resyncPreview.affected);

      if (resyncMode === 'outdated') {
        // Timestamp auf 1970-01-01 setzen, damit der Sync REPLACE auswaehlt.
        // Wir gehen ueber die ids aus der Vorschau, damit das Update genau
        // die in der Vorschau gezeigten Datensaetze trifft (Konsistenz mit
        // dem, was der Anwender bestaetigt hat). Chunked wegen .in()-Limits.
        const STALE_TS = '1970-01-01T00:00:00.000Z';
        let updatedCount = 0;
        const UPD_CHUNK = 500;
        for (let i = 0; i < resyncPreview.ids.length; i += UPD_CHUNK) {
          const slice = resyncPreview.ids.slice(i, i + UPD_CHUNK);
          const { error: updErr } = await supabase
            .from('datev_documents')
            .update({ datev_timestamp: STALE_TS })
            .in('id', slice);
          if (updErr) {
            setResyncMessage(`Fehler beim Update: ${updErr.message} (${updatedCount}/${resyncPreview.ids.length} bereits aktualisiert)`);
            setResyncMessageKind('error');
            return;
          }
          updatedCount += slice.length;
        }

        setResyncMessage(`${updatedCount} Dokument${updatedCount === 1 ? '' : 'e'} als veraltet markiert. Klicken Sie unten auf "Jetzt syncen", um die Daten neu von DATEV abzuholen.`);
        setResyncMessageKind('success');
        setResyncPreview(null);
      } else {
        // missing: Storage-Files loeschen, dann DB-Eintraege loeschen
        let storageOk = 0;
        let storageErr = 0;
        if (resyncPreview.storagePaths.length > 0) {
          // Supabase remove() akzeptiert Arrays, aber wir chunken trotzdem
          const CHUNK = 100;
          for (let i = 0; i < resyncPreview.storagePaths.length; i += CHUNK) {
            const slice = resyncPreview.storagePaths.slice(i, i + CHUNK);
            const { error: rmErr } = await supabase
              .storage
              .from('datev-documents')
              .remove(slice);
            if (rmErr) {
              storageErr += slice.length;
              console.error('[Resync] Storage-Remove Fehler:', rmErr);
            } else {
              storageOk += slice.length;
            }
          }
        }

        // DB-Eintraege loeschen (chunked, weil .in() Limits hat)
        let dbDeleted = 0;
        const DB_CHUNK = 500;
        for (let i = 0; i < resyncPreview.ids.length; i += DB_CHUNK) {
          const slice = resyncPreview.ids.slice(i, i + DB_CHUNK);
          const { error: delErr } = await supabase
            .from('datev_documents')
            .delete()
            .in('id', slice);
          if (delErr) {
            setResyncMessage(`Fehler beim DB-Delete: ${delErr.message} (${dbDeleted}/${resyncPreview.ids.length} bereits geloescht)`);
            setResyncMessageKind('error');
            return;
          }
          dbDeleted += slice.length;
        }

        const parts: string[] = [];
        parts.push(`${dbDeleted} DB-Eintrag${dbDeleted === 1 ? '' : 'e'} geloescht`);
        parts.push(`${storageOk} Storage-File${storageOk === 1 ? '' : 's'} geloescht`);
        if (storageErr > 0) parts.push(`${storageErr} Storage-Fehler`);
        parts.push('Klicken Sie unten auf "Jetzt syncen", um die Daten neu von DATEV abzuholen.');
        setResyncMessage(parts.join(' · '));
        setResyncMessageKind(storageErr > 0 ? 'error' : 'success');
        setResyncPreview(null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setResyncMessage(`Fehler bei Ausfuehrung: ${msg}`);
      setResyncMessageKind('error');
    } finally {
      setResyncBusy(false);
    }
  };

  // Filter-Reset bei Aenderungen invalidiert die Vorschau
  const onResyncFilterChange = () => {
    setResyncPreview(null);
    setResyncMessage(null);
  };

  // Optionen fuer Period-Dropdown: aktueller Monat + 12 Monate Vergangenheit
  const resyncPeriodOptions: { value: string; label: string }[] = [];
  {
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const label = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(d);
      resyncPeriodOptions.push({ value, label });
    }
  }

  const getEscalationBadge = (level: number) => {
    if (level === 0) return null;
    const colors = {
      1: { bg: '#FEF3C7', color: '#92400E', label: 'Warnung L1' },
      2: { bg: '#FED7AA', color: '#9A3412', label: 'Prüfen L2' },
      3: { bg: '#FEE2E2', color: '#991B1B', label: 'FINAL L3' }
    };
    const c = colors[level as 1 | 2 | 3] || colors[1];
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.color,
        marginLeft: '8px'
      }}>
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      pending: { bg: '#F3F4F6', color: '#6B7280' },
      partially_fulfilled: { bg: '#DBEAFE', color: '#1E40AF' },
      completed: { bg: '#D1FAE5', color: '#065F46' },
      escalated: { bg: '#FEE2E2', color: '#991B1B' },
      running: { bg: '#DBEAFE', color: '#1E40AF' },
      failed: { bg: '#FEE2E2', color: '#991B1B' },
      skipped: { bg: '#F3F4F6', color: '#6B7280' }
    };
    const s = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 500,
        backgroundColor: s.bg,
        color: s.color
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Lade Sync-Status...
      </div>
    );
  }

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px'
  };

  const headerStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '13px',
    backgroundColor: 'white',
    color: '#111827'
  };

  return (
    <div>
      {/* Audit-Resync */}
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>Audit-Resync &mdash; DATEV-Spiegel selektiv invalidieren</span>
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#6B7280' }}>
            {beraternummer}-{mandantennummer}
          </span>
        </div>

        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '14px', lineHeight: 1.5 }}>
          Markiert ausgewaehlte Dokumente im lokalen Spiegel als veraltet oder
          loescht sie. Beim naechsten Sync ruft das System sie erneut von DATEV
          ab. Pflicht-Schritt: Vorschau klicken, dann ausfuehren.
        </div>

        {/* Filter-Zeile */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Periode</label>
            <select
              value={resyncPeriod}
              onChange={e => { setResyncPeriod(e.target.value); onResyncFilterChange(); }}
              disabled={resyncBusy}
              style={{ ...inputStyle, minWidth: '180px' }}
            >
              <option value="">Alle Perioden</option>
              {resyncPeriodOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Dokumententyp</label>
            <select
              value={resyncDocType}
              onChange={e => { setResyncDocType(e.target.value); onResyncFilterChange(); }}
              disabled={resyncBusy}
              style={{ ...inputStyle, minWidth: '160px' }}
            >
              <option value="all">Alle Typen</option>
              {ALL_DOCUMENT_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Personalnummer (optional)</label>
            <input
              type="number"
              min="0"
              value={resyncEmployeeNumber}
              onChange={e => { setResyncEmployeeNumber(e.target.value); onResyncFilterChange(); }}
              disabled={resyncBusy}
              placeholder="leer = alle"
              style={{ ...inputStyle, width: '160px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Modus</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', height: '30px' }}>
              <label style={{ fontSize: '13px', color: '#111827', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="resyncMode"
                  value="outdated"
                  checked={resyncMode === 'outdated'}
                  onChange={() => { setResyncMode('outdated'); onResyncFilterChange(); }}
                  disabled={resyncBusy}
                />
                Veraltet markieren
              </label>
              <label style={{ fontSize: '13px', color: '#111827', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="resyncMode"
                  value="missing"
                  checked={resyncMode === 'missing'}
                  onChange={() => { setResyncMode('missing'); onResyncFilterChange(); }}
                  disabled={resyncBusy}
                />
                Aus Spiegel entfernen
              </label>
            </div>
          </div>
        </div>

        {/* Warnung fuer "missing"-Modus */}
        {resyncMode === 'missing' && (
          <div style={{
            padding: '10px 12px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#991B1B',
            marginBottom: '14px'
          }}>
            <strong>Achtung:</strong> Modus &quot;Aus Spiegel entfernen&quot; loescht DB-Eintraege
            UND Storage-Files. Erst nach erfolgreichem Re-Sync sind die Dateien
            wieder verfuegbar.
          </div>
        )}

        {/* Vorschau-Ergebnis */}
        {resyncPreview && (
          <div style={{
            padding: '12px',
            backgroundColor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            marginBottom: '14px',
            fontSize: '13px'
          }}>
            <div style={{ fontWeight: 600, color: '#111827', marginBottom: '6px' }}>
              Vorschau: {resyncPreview.count} Dokument{resyncPreview.count === 1 ? '' : 'e'} betroffen
            </div>
            {resyncPreview.sample.length > 0 && (
              <div style={{ color: '#6B7280', fontSize: '12px' }}>
                Stichprobe (max 10):
                <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {resyncPreview.sample.map((s, idx) => (
                    <li key={idx}>
                      {s.document_type} &middot; {s.period} &middot; MA {s.employee_number ?? '-'}
                    </li>
                  ))}
                  {resyncPreview.count > resyncPreview.sample.length && (
                    <li style={{ fontStyle: 'italic' }}>
                      &hellip; und {resyncPreview.count - resyncPreview.sample.length} weitere
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={runResyncPreview}
            disabled={resyncBusy}
            style={{
              padding: '8px 16px',
              backgroundColor: resyncBusy ? '#9CA3AF' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: resyncBusy ? 'not-allowed' : 'pointer'
            }}
          >
            {resyncBusy ? 'Laeuft...' : 'Vorschau'}
          </button>
          <button
            onClick={runResyncExecute}
            disabled={resyncBusy || !resyncPreview || resyncPreview.count === 0}
            style={{
              padding: '8px 16px',
              backgroundColor: (resyncBusy || !resyncPreview || resyncPreview.count === 0)
                ? '#9CA3AF'
                : (resyncMode === 'missing' ? '#DC2626' : '#F59E0B'),
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: (resyncBusy || !resyncPreview || resyncPreview.count === 0) ? 'not-allowed' : 'pointer'
            }}
          >
            Ausfuehren
          </button>
          {!hasResyncFilterConcrete() && (
            <span style={{ fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
              Mindestens ein Filter muss gesetzt sein.
            </span>
          )}
        </div>

        {/* Status-Message */}
        {resyncMessage && (
          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            backgroundColor:
              resyncMessageKind === 'success' ? '#ECFDF5' :
              resyncMessageKind === 'error' ? '#FEF2F2' : '#EFF6FF',
            border: `1px solid ${
              resyncMessageKind === 'success' ? '#A7F3D0' :
              resyncMessageKind === 'error' ? '#FECACA' : '#DBEAFE'
            }`,
            borderRadius: '6px',
            fontSize: '13px',
            color:
              resyncMessageKind === 'success' ? '#065F46' :
              resyncMessageKind === 'error' ? '#991B1B' : '#1E40AF'
          }}>
            {resyncMessage}
          </div>
        )}
      </div>

      {/* Manueller Sync-Trigger */}
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>Manueller Sync für diesen Mandanten</span>
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#6B7280' }}>
            {beraternummer}-{mandantennummer}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={triggerManualSync}
            disabled={syncTriggering}
            style={{
              padding: '10px 20px',
              backgroundColor: syncTriggering ? '#9CA3AF' : '#2563EB',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: syncTriggering ? 'not-allowed' : 'pointer'
            }}
          >
            {syncTriggering ? 'Tasks werden erzeugt...' : 'Jetzt syncen'}
          </button>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>
            Erzeugt Sync-Tasks (+1/-4 Monate). Worker arbeitet sie asynchron ab. Fortschritt siehe Card &quot;DATEV-Sync-Queue&quot; unten.
          </span>
        </div>
        {syncMessage && (
          <div style={{
            marginTop: '12px',
            padding: '10px 12px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #DBEAFE',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#1E40AF'
          }}>
            {syncMessage}
          </div>
        )}
      </div>

      {/* Stufe 2: DATEV-Sync-Queue (Live-Counts der Tasks fuer diesen Mandanten) */}
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>DATEV-Sync-Queue</span>
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#6B7280' }}>
            {beraternummer}-{mandantennummer}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Wartend</div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: taskCounts.pending > 0 ? '#F59E0B' : '#111827' }}>
              {taskCounts.pending}
            </div>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>In Bearbeitung</div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: taskCounts.running > 0 ? '#2563EB' : '#111827' }}>
              {taskCounts.running}
            </div>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Heute abgeschlossen</div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#065F46' }}>
              {taskCounts.completed_today}
            </div>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Heute fehlgeschlagen</div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: taskCounts.failed_today > 0 ? '#DC2626' : '#111827' }}>
              {taskCounts.failed_today}
            </div>
          </div>
        </div>
        {(taskCounts.pending > 0 || taskCounts.running > 0) && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>
            Worker laeuft jede Minute. Fortschritt aktualisiert sich automatisch alle 5 Sekunden.
          </div>
        )}
      </div>

      {/* Offene Pending Tasks */}
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>Offene Pending Tasks</span>
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#6B7280' }}>
            {pendingTasks.length} aktiv
          </span>
        </div>
        {pendingTasks.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            Keine offenen Tasks für diesen Mandanten.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Zeitraum</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Task</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Fehlende Dokumente</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontWeight: 500 }}>Prüfungen</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Alter</th>
              </tr>
            </thead>
            <tbody>
              {pendingTasks.map(t => {
                const ageDays = Math.floor((Date.now() - new Date(t.created_at).getTime()) / (1000 * 60 * 60 * 24));
                const missingCurrent = (t.required_documents || []).filter(d => !(t.fulfilled_documents || []).includes(d));
                const missingNext = (t.required_next_month_documents || []).filter(d => !(t.fulfilled_documents || []).includes(`next:${d}`));
                const allMissing = [
                  ...missingCurrent,
                  ...missingNext.map(d => `${d} (Folgemonat)`)
                ];

                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '10px 4px', color: '#111827' }}>
                      {formatPeriod(t.year, t.month)}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#111827' }}>
                      {t.task_type}
                      {getEscalationBadge(t.escalation_level)}
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      {getStatusBadge(t.status)}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#6B7280', maxWidth: '300px' }}>
                      {allMissing.length > 0 ? allMissing.join(', ') : '(nur next-month offen)'}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#6B7280', textAlign: 'right' }}>
                      {t.check_count}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                      {ageDays} Tage
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Kritische Ersetzungen letzte 7 Tage */}
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>Kritische Ersetzungen (LSTB / SVNW, letzte 7 Tage)</span>
          <span style={{ fontSize: '13px', fontWeight: 400, color: '#6B7280' }}>
            {replacements.length} Ersetzungen
          </span>
        </div>
        {replacements.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            Keine kritischen Ersetzungen in den letzten 7 Tagen.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Zeitpunkt</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Dokument</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Mitarbeiter</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Zeitraum</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Alte Version</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Neue Version</th>
              </tr>
            </thead>
            <tbody>
              {replacements.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                    {formatDateTime(r.created_at)}
                  </td>
                  <td style={{ padding: '10px 4px', color: '#111827', fontWeight: 500 }}>
                    {r.document_type}
                  </td>
                  <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                    {r.employee_number ?? '-'}
                  </td>
                  <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                    {r.period}
                  </td>
                  <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                    {r.event_data?.previous_datev_timestamp
                      ? formatDateTime(r.event_data.previous_datev_timestamp)
                      : '-'}
                  </td>
                  <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                    {r.event_data?.new_datev_timestamp
                      ? formatDateTime(r.event_data.new_datev_timestamp)
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Sync-Run-History (global) */}
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span>Sync-Run-Historie (alle Mandanten, letzte 20)</span>
        </div>
        {syncRuns.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            Noch keine Runs aufgezeichnet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Start</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Trigger</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 500 }}>Status</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontWeight: 500 }}>Kunden</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontWeight: 500 }}>Tasks angelegt</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontWeight: 500 }}>davon offen</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontWeight: 500 }}>davon fehlgeschlagen</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontWeight: 500 }}>Dauer</th>
              </tr>
            </thead>
            <tbody>
              {syncRuns.map(run => {
                // Stufe 2: bevorzugt task_count anzeigen (aggregiert aus datev_sync_tasks).
                // Bei Legacy-Runs (vor Producer-Umbau) ist task_count undefined - dann
                // zeigen wir totals_created als historischen Fallback (Anzahl beim Run direkt erstellte Docs).
                const t = run.task_count;
                const tasksCreated = t ? t.total : run.totals_created;
                const tasksOpen = t ? (t.pending + t.running) : 0;
                const tasksFailed = t ? t.failed : run.totals_failed;
                const isLegacy = !t && (run.totals_created > 0 || run.totals_replaced > 0);

                return (
                  <tr key={run.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '10px 4px', color: '#111827' }}>
                      {formatDateTime(run.started_at)}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#6B7280' }}>
                      {run.trigger_type}
                      {isLegacy && (
                        <span style={{ marginLeft: '6px', fontSize: '10px', color: '#9CA3AF', fontStyle: 'italic' }}>
                          (legacy)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 4px' }}>
                      {getStatusBadge(run.status)}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#6B7280', textAlign: 'right' }}>
                      {run.synced_customers ?? '-'}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#111827', textAlign: 'right' }}>
                      {tasksCreated}
                    </td>
                    <td style={{ padding: '10px 4px', color: tasksOpen > 0 ? '#F59E0B' : '#6B7280', textAlign: 'right' }}>
                      {tasksOpen}
                    </td>
                    <td style={{ padding: '10px 4px', color: tasksFailed > 0 ? '#DC2626' : '#6B7280', textAlign: 'right' }}>
                      {tasksFailed}
                    </td>
                    <td style={{ padding: '10px 4px', color: '#6B7280', textAlign: 'right' }}>
                      {formatDuration(run.duration_ms)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
